import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Bot, Zap, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import { currency, fmtTimeAgo } from "../lib/format";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import SnacksFundTracker from "../components/SnacksFundTracker";

const AMOUNTS = [5, 10, 25, 50, 100];

export default function RobotSnacks() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [amt, setAmt] = useState(10);
    const [custom, setCustom] = useState("");
    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState(null);

    useEffect(() => {
        api.get("/robot-snacks/current").then((r) => setData(r.data));
        api.get("/stripe/onboarding").then((r) => setMode(r.data.mode));
    }, []);
    useEffect(() => { if (user) { setName(user.name); setEmail(user.email); } }, [user]);

    async function submit() {
        const amount = parseFloat(custom || amt);
        if (!amount || amount <= 0 || amount >= 1000) return toast.error("Snack amount must be between $1 and $999");
        if (!name.trim()) return toast.error("Add your name so we can thank you");
        setBusy(true);
        try {
            const { data: res } = await api.post("/payments/checkout-snacks", {
                amount, donor_name: name, email: email || null, message: message || null,
                origin_url: window.location.origin,
            });
            if (res.checkout_url) { window.location.href = res.checkout_url; return; }
            toast.error("Could not start checkout");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Snack machine is offline");
        }
        setBusy(false);
    }

    if (!data) return <div className="p-16 text-center text-[var(--rl-text-2)]">Waking the robot…</div>;

    if (!data.active) {
        return (
            <div className="max-w-[700px] mx-auto px-6 py-24 text-center" data-testid="snacks-page-inactive">
                <div className="w-16 h-16 rounded-full bg-[var(--rl-success)] text-white flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-8 h-8" />
                </div>
                <h1 className="font-serif text-4xl mt-6">The robot is well-fed.</h1>
                <p className="text-[var(--rl-text-2)] mt-3">No active snacks ask right now — API credits are healthy. We'll ring the bell again when the meter runs low.</p>
                <Link to="/"><Button className="btn-primary rounded-full mt-6 h-11 px-5">Back to RescueLink</Button></Link>
            </div>
        );
    }

    const pct = Math.min(100, Math.round((data.funded_usd / data.ask.target_usd) * 100));

    return (
        <div data-testid="robot-snacks-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
                <SnacksFundTracker />
            </div>
            <div className="bg-[var(--rl-cream)] border-b border-[var(--rl-border)] relative overflow-hidden mt-6">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                        <div className="eyebrow mb-3 inline-flex items-center gap-2"><Bot className="w-4 h-4" /> Robot Snacks</div>
                        <h1 className="font-serif text-4xl lg:text-6xl tracking-tight leading-[1.05]">
                            Feed the robot.<br /><span className="text-[var(--rl-primary)]">Save more dogs.</span>
                        </h1>
                        <p className="mt-4 text-[var(--rl-text-2)] max-w-xl leading-relaxed">
                            Hi — I&apos;m the AI behind RescueLink&apos;s matchmaking, dog-bio translation, and rescue analysis. Every action I take costs a fraction of a cent on an LLM meter. Every dollar contributes to the rescuelink.earth server fund — the maintenance threshold is <strong>$1,400</strong>, and when we&apos;re above it every rescue tool stays free.
                        </p>
                        <div className="mt-6 rounded-xl bg-white/70 border border-[var(--rl-border)] p-5" data-testid="snacks-reason">
                            <div className="eyebrow text-[var(--rl-primary)]">Current ask</div>
                            <div className="font-serif text-2xl mt-1">{data.ask.reason}</div>
                            <p className="text-sm text-[var(--rl-text-2)] mt-2">{data.ask.description}</p>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl bg-white border border-[var(--rl-border)] p-6">
                            <div className="flex items-baseline justify-between">
                                <div>
                                    <div className="font-serif text-4xl text-[var(--rl-primary)]">{currency(data.funded_usd)}</div>
                                    <div className="text-xs text-[var(--rl-text-3)] uppercase tracking-widest mt-1">of {currency(data.ask.target_usd)} goal</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-serif text-3xl">{pct}%</div>
                                    <div className="text-xs text-[var(--rl-text-3)] uppercase tracking-widest mt-1">funded</div>
                                </div>
                            </div>
                            <div className="mt-4 h-3 rounded-full bg-[var(--rl-cream)] overflow-hidden" data-testid="snacks-progress">
                                <div className="h-full bg-[var(--rl-primary)] transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="mt-4 flex items-center gap-3 text-xs text-[var(--rl-text-3)]">
                                <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3" /> {data.count || 0} snack{data.count === 1 ? "" : "s"} bought</span>
                                <span>·</span>
                                <span>{mode === "live" ? "Live payments" : "Test mode"}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7">
                    <div className="card-warm rounded-2xl p-8">
                        <div className="eyebrow mb-3 inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[var(--rl-primary)]" /> Buy a snack</div>
                        <div className="grid grid-cols-5 gap-2 mt-2">
                            {AMOUNTS.map((a) => (
                                <button key={a} onClick={() => { setAmt(a); setCustom(""); }}
                                        data-testid={`snack-amt-${a}`}
                                        className={`h-14 rounded-xl border font-serif text-xl transition ${amt === a && !custom ? "border-[var(--rl-primary)] bg-[#fff5f3] text-[var(--rl-text)]" : "border-[var(--rl-border)] hover:bg-[var(--rl-cream)]"}`}>
                                    ${a}
                                </button>
                            ))}
                        </div>
                        <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom (max $999)"
                               className="mt-3 border-[var(--rl-border)] h-11" data-testid="snack-custom" />

                        <div className="mt-5 grid gap-4">
                            <div>
                                <label className="eyebrow">Your name</label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 border-[var(--rl-border)]" data-testid="snack-name" />
                            </div>
                            <div>
                                <label className="eyebrow">Email (for receipt)</label>
                                <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 border-[var(--rl-border)]" data-testid="snack-email" />
                            </div>
                            <div>
                                <label className="eyebrow">Note to the robot (optional)</label>
                                <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
                                          placeholder="e.g. 'Kimchi's translation made me cry.'"
                                          className="mt-1.5 border-[var(--rl-border)]" data-testid="snack-message" />
                            </div>
                            <Button onClick={submit} disabled={busy} className="btn-primary w-full rounded-full h-12" data-testid="snack-submit">
                                {busy ? "Warming up the meter…" : <>Feed the robot {currency(parseFloat(custom || amt))} <ArrowRight className="w-4 h-4 ml-2" /></>}
                            </Button>
                            <div className="text-xs text-[var(--rl-text-3)] text-center">Charged securely via Stripe · {mode === "live" ? "Live" : "Test mode"} · Every dollar is line-itemed below.</div>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-5">
                    <div className="eyebrow mb-3">Recent snacks</div>
                    <div className="space-y-2" data-testid="snack-donors">
                        {data.recent_donors.length === 0 ? (
                            <div className="card-warm rounded-lg p-5 text-center text-sm text-[var(--rl-text-2)]">Be the first to feed the robot.</div>
                        ) : data.recent_donors.map((d, i) => (
                            <div key={i} className="card-warm rounded-lg p-4 flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-[var(--rl-cream)] flex items-center justify-center text-xs font-semibold shrink-0">{(d.donor_name || "?")[0]}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm">
                                        <span className="font-semibold">{d.donor_name}</span>
                                        <span className="text-[var(--rl-primary)] font-semibold ml-2">{currency(d.amount)}</span>
                                    </div>
                                    {d.message && <div className="text-xs text-[var(--rl-text-2)] italic mt-0.5">"{d.message}"</div>}
                                    <div className="text-[10px] text-[var(--rl-text-3)] mt-0.5">{fmtTimeAgo(d.updated_at)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 rounded-xl bg-[var(--rl-cream)] border border-[var(--rl-border)] p-4 text-xs text-[var(--rl-text-2)]">
                        <strong className="text-[var(--rl-text)]">Transparent by design:</strong> Robot Snacks fund <em>only</em> the platform's LLM + server costs. They do not touch the Global Fund used for rescue operations. Every snack is a separate line item in Stripe.
                    </div>
                </div>
            </section>
        </div>
    );
}
